import * as React from "react";

import { ReactorConfiguration } from "@app/arc";
import { History } from "history";
import { ProjectService, ProjectTileData } from "../projects";
import { ProjectRunConfigurationStatus, ProjectRunStatusEnum } from "../projects/enumerators";
import { FilteredResource, Resource, ResourceService, ResourceStatus, ResourceType } from "../resources";
import { TooltipWrapper } from "../shared";
import { RoleRestricted, Roles } from "../user";
import { JSX } from "react";

const handleArchiveConfirm = async (isArchived: boolean, resourceId: number, onArchived?: (resourceId: number) => void): Promise<void> => {
  const resourceService = new ResourceService();
  if (isArchived) {
    await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Active);
  } else {
    await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Archived);
  }

  if (onArchived) {
    onArchived(resourceId);
  }
};

const handleHiddenChange = async (resourceId: number, onHidden?: (resourceId: number) => void): Promise<void> => {
  const resourceService = new ResourceService();
  await resourceService.updateResourceHiddenStatus(resourceId);

  if (onHidden) {
    onHidden(resourceId);
  }
};

const handleClone = (history: History<unknown>, resourceId: number): void => {
  history.push("/projects/" + resourceId + "/clone");
};

const handleCancelQueuedProject = async (projectId: number): Promise<void> => {
  const projectService = new ProjectService();
  await projectService.cancelQueuedProject(projectId);
};

const handleUpdateSummary = async (
  onUpdateProjectOptions: (summary: ProjectTileData) => void,
  projectResource: Resource,
  filteredResource: FilteredResource,
): Promise<void> => {
  const projectService = new ProjectService();
  //re-retrieve the summary results since the project was updated
  const projectDashboardModel = await projectService.projectTileResults([filteredResource],
    projectResource.isArchived ? ResourceStatus.Archived : ResourceStatus.Active);

  if (projectDashboardModel
    && projectDashboardModel.projectTileAggregates
    && projectDashboardModel.projectTileAggregates.length === 1) {
    //pass back to the Home component to refresh its data and re-render
    onUpdateProjectOptions(projectDashboardModel.projectTileAggregates[0]);
  }
};

const buildReactorConfig = (projectTileData: ProjectTileData, useCompanionAdequacy?: boolean): ReactorConfiguration | undefined => {
  // TODO: Temporary until re-aggregation is done on the fly for projection and specialty group exception changes
  // TODO: Join with ProjectTile.tsx's Composite implementation.
  if (projectTileData) {
    const r = projectTileData;
    const a = useCompanionAdequacy ? r.companionAdequacy : r.adequacy;
    const showAttestation = r.showAttestation &&
      ((a && a.recentlyAttestedUniqueNPIPercent !== undefined) ||
        (r && r.accuracy));

    //account for accuracy-only projects when checking for attestation
    const attestationPercent = a && a.recentlyAttestedUniqueNPIPercent
      ? a.recentlyAttestedUniqueNPIPercent
      : r && r.accuracy && r.accuracy.recentlyAttestedPercent
        ? r.accuracy.recentlyAttestedPercent
        : undefined;

    const conf: ReactorConfiguration = {
      accuracy: null,
      adequacy: a && {
        blue: a.percentPassByException || 0,
        green: a.percentPassBoth || 0,
        index: a.adequacyIndex !== null && a.adequacyIndex !== undefined ? a.adequacyIndex : "N/A",
        projected: a.projectedPercentPassBoth || null,
        projectedIndex: a.projectedAdequacyIndex || null,
        red: a.percentPassNone || 0,
        yellow: ((a.percentPassAccessOnly || 0) + a.percentPassServicingOnly) || 0,
      } || null,
      attestation: showAttestation && ({
        green: attestationPercent || 0,
        index: attestationPercent || 0,
        isPercent: true,
        red: 0,
        yellow: 0,
      }) || null,
      composite: null,
      compositeOverride: "adequacy",
    };

    return conf;
  }

  return undefined;
};

interface ProjectTileState {
  projectResource: Resource;
  sidePanelArchiveWarningVisible: boolean;
  summary: ProjectTileData;
  triggerHide: () => void;
  triggerOpen: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const renderProjectStatus = (projectData: ProjectTileData, isTile?: boolean, userRoles?: string[]): JSX.Element => {
  if (!projectData) {
    return;
  }

  const runConfigurationStatus = projectData.runConfigurationStatus
    ? projectData.runConfigurationStatus
    : ProjectRunConfigurationStatus.Created;

  let statusText: string;
  let statusTipText: string;
  let statusColor: string;
  let statusFAIcon: string;
  let extraIcon: JSX.Element;

  switch (runConfigurationStatus) {
    case ProjectRunConfigurationStatus.Created:
      statusText = "Ready";
      statusTipText = "Project Valid and Ready to Run";
      statusColor = "text-success";
      statusFAIcon = "fa-check-circle";
      break;

    case ProjectRunConfigurationStatus.Succeeded:
      statusText = "Complete";
      statusTipText = "Project Completed Successfully";
      statusColor = "text-success";
      statusFAIcon = "fa-check-circle";

      const projectionProcessor = userRoles?.includes(Roles.PROJECTION_PROCESSOR);
      const termAdmin = userRoles?.includes(Roles.TERM_PROVIDER_ADMIN);
      const exceptionManager = userRoles?.includes(Roles.EXCEPTION_MANAGER);

      const isZipProject = projectData.resource.resourceType === ResourceType.ZipAnalysisProject;
      const projectHasStatus = !projectData.isRunning && projectData.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed;
      const showTermMessage = termAdmin && projectData.hasUnprocessedProviderTerminations && projectHasStatus;
      const showProjectionMessage = projectionProcessor && projectData.hasUnprocessedProjectionProviders && projectHasStatus;
      const showUnprocessedSpecialtyGroupExceptions = exceptionManager && projectData.hasUnprocessedSpecialtyGroupExceptions && projectHasStatus;

      const messageArray = [];
      const roleArray = [];

      if (!isZipProject) {
        if (showTermMessage) {
          messageArray.push("Unprocessed Provider Terminations");
          roleArray.push(Roles.TERM_PROVIDER_ADMIN);
        }
        if (showProjectionMessage) {
          messageArray.push("Unprocessed Targeted or Excluded Providers");
          roleArray.push(Roles.PROJECTION_PROCESSOR);
        }
        if (showUnprocessedSpecialtyGroupExceptions) {
          messageArray.push("Unprocessed Specialty Group Exceptions");
          roleArray.push(Roles.EXCEPTION_MANAGER);
        }

        extraIcon = isTile && projectHasStatus &&
          (showTermMessage || showProjectionMessage || showUnprocessedSpecialtyGroupExceptions ?
            <RoleRestricted allowedRoles={roleArray}>
              <TooltipWrapper text={messageArray.join(" and ")}>
                <i className="fa fa-exclamation-triangle text-yellow" />
              </TooltipWrapper>
            </RoleRestricted> :
            null);
      }
      break;

    case ProjectRunConfigurationStatus.Queued:
      statusText = "Queued";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      statusTipText = "Project Queued Up to Run";
      break;

    case ProjectRunConfigurationStatus.Analyzing:
    case ProjectRunConfigurationStatus.Aggregating:
    case (ProjectRunStatusEnum.Running as unknown as ProjectRunConfigurationStatus):
      statusText = "Running";
      statusTipText = "Project Currently Running";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.Canceled:
      statusText = "Canceled";
      statusColor = "text-danger";
      statusFAIcon = "fa-times-circle";
      statusTipText = "Project Canceled While Running";
      break;

    case ProjectRunConfigurationStatus.Failed:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusFAIcon = "fa-times-circle";
      break;

    case ProjectRunConfigurationStatus.FailedProviderChanges:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusFAIcon = "fa-times-circle";
      break;

    case ProjectRunConfigurationStatus.QueuedProviderChanges:
      statusText = "Queued";
      statusTipText = "Project Queued to Process Provider Changes";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.ProcessingProviderChanges:
      statusText = "Running";
      statusTipText = "Project Currently Processing Provider Changes";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis:
      statusText = "Queued";
      statusTipText = "Project Queued to Update Results with Specialty Group Exception Change";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis:
      statusText = "Running";
      statusTipText = "Project Currently Updating Results with Specialty Group Exception Change";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.QueuedProspectMarketNcp:
      statusText = "Queued";
      statusTipText = "Project Queued to Process Next Closest Providers";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.ProcessingProspectMarketNcp:
      statusText = "Running";
      statusTipText = "Project Currently Processing Next Closest Providers";
      statusColor = "text-info";
      statusFAIcon = "fa-spinner fa-spin";
      break;

    case ProjectRunConfigurationStatus.FailedProspectMarketNcp:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusFAIcon = "fa-times-circle";
      break;
  }

  return (
    <p className={`${statusColor} mb-0`} data-toggle="tooltip" title={statusTipText}>
      {projectData.isReadOnly && <i className="fa fa-book mr-2 text-dark" title="Read-Only Mode" />}
      <i className={`fa ${statusFAIcon}`} />
      &nbsp;{statusText}&nbsp;
      {extraIcon}
      {
        (projectData.runConfigurationStatus &&
          projectData.runConfigurationStatus === ProjectRunConfigurationStatus.Succeeded &&
          projectData.hasUnpublishedResults || null) &&
        <span className="fs18 text-danger fa fa-eye" data-toggle="tooltip" title="Contains Unpublished data" />
      }
    </p>
  );
};

export {
  buildReactorConfig,
  handleArchiveConfirm,
  handleCancelQueuedProject,
  handleClone,
  handleHiddenChange,
  handleUpdateSummary,
  ProjectTileState,
  renderProjectStatus,
};
